import React, {useEffect, useRef, useState} from 'react';
import {Badge, Checkbox, Col, Form, message, Row, Spin, Tabs} from 'antd';
import ProForm, {ProFormInstance} from '@ant-design/pro-form';
import {FileUploadUtility, WdiFileList} from '@/components/WdiControls/WdiFileUpload';
import TabPane from '@ant-design/pro-card/lib/components/TabPane';
import {FormPageProps, WdiFormPageTitle} from '@/components/WdiBus/FormPage';
import {initApprovalPageContext, ViewEntityProps} from '../../components/data';
import {ResponseStatus} from '@/utils/request';
import DataStore from '@/components/GridWidget/DataStore';
import {DataOptions} from '@/services/basic/metadata';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiCardTitle, wdiCore, WdiInput, WdiStaffCard} from '@/components/WdiControls';
import {Enum} from '@/enums/system';
import FormSaMktInfosApp from '@/pages/mkt/sa/components/FormSaMktInfos';
import {PaymentMktDetailsForApprovalPanel} from '../../GeneralPaymentApply/components/detail';
import DynamicInformation from '../../components/DynamicInformation';
import {FormApi} from '@/services/sales';
import {VatType} from "@/services/mdm/vattype";
import {Supplier} from "@/services/mdm/supplier";
import {Invoices} from "@/services/sales/financial/invoices";
import {FormInvoiceForApprovalPanel} from "@/pages/sales/sp/components/FormInvoice";
import {Company} from "@/services/mdm/company";
import {BankAccount} from "@/services/mdm/bankaccount";
import {SPMktWarningApprovalApp} from "@/pages/mkt/sp/components/WarningInformation";
import {FormSpMkt} from "@/services/mkt/sp";
import {OaForm} from "@/services/mdm/oaform";
import {OaFormApp} from "@/pages/sales/sp/components/OaContractCmpt";
import {Staff} from '@/services/basic/staff';
import {WdiEditor} from "@/components/WdiControls/WdiEditor";

const moreConfig: any = {
  successParams: [
    {
      url: '/basicapi/favorite/getButtons',
      keys: [],
    }, {
      url: '/expenseapi/spMktButtons/get',
      keys: [
        'UndoButton',
        'ReeditButton',
        'CancelButton',
        'SupplementFileButton',
        'InterveneFlowButton',
        'ModifyVoucherNoButton',
        'AppendExtNodeButton',
        'FormSpPrintButton',
        'AddMailApproverButton',
        "ExpediteFlowButton",
      ],
    },
  ],
  errorParams: [
    {
      url: '/expenseapi/spMktButtons/get',
      keys: [
        'RepairButton',
        'InterveneFlowButton',
      ],
    },
  ],
};

const approvalConfig: any = {
  params: {
    url: '/expenseapi/spMktButtons/get',
    keys: [
      "ApprovalButton",
      "ReadButton",
    ],
  },
};

const PaymentMktApprovalApp: React.FC<FormPageProps> = (props) => {
  const formRef = useRef<ProFormInstance>();
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [entity, setEntity] = useState<ViewEntityProps>();
  const [invoiceDataSource, setInvoiceDataSource] = useState<any[]>([]);
  const [warningCount, setWarningCount] = useState<number>(0);
  const [activeKey, setActiveKey] = useState<string>("1");

  useEffect(() => {
    if (!formRef?.current) {
      return;
    }
    setActiveKey(props.data?.defaultActiveKey ?? "1");
  }, [!!(formRef?.current)])

  const onDetailsChange = (data: any[]) => {
    setDataSource(data);
    FormApi.get(props.data?.formId || 0).then(responseResult => {
      setEntity(Object.assign({...entity}, {form: responseResult.data}));
      formRef.current?.setFieldsValue({
        amountInVat: wdiCore.Number.AmountToString(responseResult.data.amount),
      });
    });
  };

  useEffect(() => {
    (async () => {
      const params = props.data;
      if (params) {
        const result = await initApprovalPageContext(params);
        if (result.code !== ResponseStatus.SUCCESS) {
          message.error(result.message);
          return;
        }
        const viewEntity: ViewEntityProps = result.data;

        setEntity(viewEntity);

        if (viewEntity.formSpMkt) {
          formRef.current?.setFieldsValue({
            amountInVat: wdiCore.Number.AmountToString(viewEntity.formSpMkt.amountInVat),
            remark: viewEntity.formSpMkt.remark,
            attachment: FileUploadUtility.parse(viewEntity.formSpMkt.fileName, viewEntity.formSpMkt.filePath),
            invoiceStatus: viewEntity.formSpMkt.invoiceStatus,
            reimburseType: viewEntity.formSpMkt.reimburseType == Enum.ReimburseType.PRE_PAY,
            reversePrepayDetail: viewEntity.formSpMkt?.reversePrepayDetail,
            isReversePrepay: viewEntity.formSpMkt.isReversePrepay,
          });
          Invoices.getForPayment(viewEntity.formSpMkt.formSpMktId || 0).then(responseResult => {
            setInvoiceDataSource(DataStore.init(responseResult.data));
          })
          Company.get(String(viewEntity?.formSpMkt?.companyCode)).then(res => {
            formRef.current?.setFieldsValue({
              companyName: res?.data?.codeOrName
            })
          })
          BankAccount.get(Number(viewEntity?.formSpMkt?.bankAccountId)).then(res => {
            formRef.current?.setFieldsValue({
              bankAccountNo: res?.data?.bankAccountNo,
              bankAccountName: viewEntity?.formSpMkt?.bankAccountName,
            })
          })
          if (viewEntity?.formSpMkt?.payToUserId && Number(viewEntity?.formSpMkt?.payToUserId)) {
            Staff.get(Number(viewEntity?.formSpMkt?.payToUserId)).then(res => {
              formRef.current?.setFieldsValue({
                payToUser: wdiCore.String.GenerateLabelDescription(res?.data?.staffNoOrName, res?.data?.title),
              })
            })
          }
          if (viewEntity?.formSpMkt?.payTypeId) {
            DataOptions.search({
              typeGroup: 106,
              inValues: String(viewEntity?.formSpMkt?.payTypeId)
            }).then(res => {
              formRef.current?.setFieldsValue({
                payTypeId: res?.data[0].name
              })
            });
          }
          FormSpMkt.Buttons.countOfWarning(viewEntity?.formSpMkt?.formSpMktId || 0).then(response => {
            setWarningCount(response.data);
          });
          if (viewEntity.formSpMkt.oaFormNo) {
            OaForm.get(viewEntity.formSpMkt?.oaFormNo).then(res => {
              formRef.current?.setFieldsValue({
                oaFormNo: res.data,
              });
            })
          }
        }
        //税率、支付方式、供应商、凭证号取第一条金额非0明细的值
        if (viewEntity.formSpMktDetails) {
          setDataSource(DataStore.init(viewEntity.formSpMktDetails));
          DataOptions.search({
            typeGroup: 5,
            inValues: String(viewEntity?.formSpMktDetails[0]?.paymentMethodId)
          }).then(res => {
            formRef.current?.setFieldsValue({
              paymentMethodName: res?.data[0].name
            })
          });
          Supplier.get(Number(viewEntity?.formSpMktDetails[0]?.supplierId)).then(res => {
            formRef.current?.setFieldsValue({
              supplierName: res?.data?.codeOrName
            })
          })
          VatType.get(Number(viewEntity?.formSpMktDetails[0]?.vatTypeId)).then(res => {
            formRef.current?.setFieldsValue({
              vatTypeId: res?.data?.vatTypeName
            })
          });
          formRef.current?.setFieldsValue({
            voucherNo: viewEntity?.formSpMktDetails[0]?.voucherNo
          })

        }
      }
    })();
  }, [props.data]);

  useEffect(() => {
    if (props.setPageTitle && entity) {
      let title = `广告类活动核销单【${entity.form?.formNo}】`;
      DataOptions.search({typeGroup: 3, value: entity.form?.statusId}).then(response => {
        if (response.code !== ResponseStatus.SUCCESS) {
          message.error(response.message);
          return;
        }
        if (response.data && response.data.length > 0) {
          if (entity.form?.statusId != Enum.FormStatus.SCRAP && entity.form?.exportStatus == Enum.ExportStatus.EXPORT_SUCCESS) {
            title += `，已导出`;
          } else {
            title += `，${response.data[0].name}`;
          }
          if (props.setPageTitle) props.setPageTitle(
            <WdiFormPageTitle
              title={title}
              onClose={props.onClose}
              callback={props.callback}
              form={entity?.form}
              useCaseId={Enum.UseCase.SP_MKT}
              moreConfig={moreConfig}
              approvalConfig={approvalConfig}/>,
          );
        }
      });
    }
  }, [props.setPageTitle, entity]);

  return (
    <>
      <WdiStaffCard formId={entity?.form?.formId}/>
      <Tabs activeKey={activeKey} onTabClick={(key, _) => setActiveKey(key)} size='large'>
        <TabPane tab={<span>核销信息</span>} key='1'>
          <ProForm
            formRef={formRef}
            submitter={{
              render: () => {
                return (<></>);
              },
            }}
          >
            <FormSaMktInfosApp formRef={formRef} defaultCollapsed={true} formSaMktId={entity?.formSpMkt?.formSaMktId}/>
            <WdiPanel title={<WdiCardTitle iconType={Enum.IconType.FORM_SP} title='活动核销单信息'/>} collapsed={true}>
              <Row gutter={[16, 24]}>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='amountInVat' label='本次核销含税总金额'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='vatTypeId' label='税率类型'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='paymentMethodName' label='支付方式'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className="gutter-row" span={4}>
                  <Form.Item name="isReversePrepay" valuePropName="checked" label="是否冲销预付">
                    <Checkbox disabled={true}/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='reversePrepayDetail' label='冲销的预付单'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='payTypeId' label='支付类型'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>


              </Row>
              <Row gutter={[16, 24]}>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='bankAccountNo' label='银行账号'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='bankAccountName' label='户名'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='companyName' label='公司'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='supplierName' label='供应商'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='payToUser' label='员工'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={4}>
                  <Form.Item name="oaFormNo" label="OA合同号">
                    <OaFormApp/>
                  </Form.Item>
                </Col>


              </Row>
              <Row gutter={[16, 24]}>
                <Col className='gutter-row' span={4}>
                  <Form.Item name='voucherNo' label='外部系统单号'>
                    <WdiInput readOnly/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={12}>
                  <Form.Item name='remark' label='报销备注'>
                    <WdiEditor readOnly={true}/>
                  </Form.Item>
                </Col>
                <Col className='gutter-row' span={6}>
                  <Form.Item
                    name='attachment'
                    label='报销附件'
                    valuePropName='fileList'
                    getValueFromEvent={(e: any) => {
                      return e.fileList || [];
                    }}
                  >
                    <WdiFileList/>
                  </Form.Item>
                </Col>
              </Row>
            </WdiPanel>
          </ProForm>
          <PaymentMktDetailsForApprovalPanel
            dataSource={dataSource}
            data={{
              form: entity?.form,
              formSpMkt: entity?.formSpMkt
            }}
            onChange={onDetailsChange}
            onClose={props.onClose}/>
          {entity?.formSpMkt?.invoiceStatus == Enum.InvoiceStatus.WITH_INVOICE && (
            <FormInvoiceForApprovalPanel
              dataSource={invoiceDataSource}
              data={{
                form: entity?.form,
                formSpMkt: entity?.formSpMkt
              }}
            />
          )}
        </TabPane>
        <TabPane
          tab={
            <>
              <span>警示信息</span>
              <Badge count={warningCount}
                     style={{
                       color: "black",
                       boxShadow: "none",
                       background: "none",
                       top: "-20px",
                       right: "-15px",
                       position: 'absolute'
                     }}>
              </Badge>
            </>
          }
          key="3"
        >
          <SPMktWarningApprovalApp formId={entity?.form?.formId}/>
        </TabPane>
        <TabPane tab={<span>动态信息</span>} key='2'>
          {
            entity?.form ? <DynamicInformation
              formId={entity?.form?.formId}
              formTypeId={entity?.form?.formTypeId}
              pageTypeId={entity?.form?.pageTypeId}
              useCaseId={Enum.UseCase.SP_MKT}
            /> : <Spin/>
          }
        </TabPane>
      </Tabs>
    </>
  );
};
export default PaymentMktApprovalApp;
